import React, {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";
import { render, unstable_batchedUpdates } from "react-dom";
import cornerstone from "cornerstone-core";
import cornerstoneMath from "cornerstone-math";
import cornerstoneTools from "cornerstone-tools";
import cornerstoneWebImageLoader from "cornerstone-web-image-loader";
import cornerstoneWADOImageLoader from "cornerstone-wado-image-loader";
import dicomParser from 'dicom-parser';
import Hammer from "hammerjs";
import {baseimgUrl} from '../Univ.js'

const mriImages = [
    baseimgUrl+"media/radiology/220px--Structural_MRI_animation.ogv.jpg",
    baseimgUrl+"media/radiology/CT.1.3.12.2.1107.5.1.7.137163.30000022032314575807000000405.jpg",
    baseimgUrl+"media/radiology/1.2.826.0.1.3680043.6.18515.32146.20220321174943.536.2.dcm.jpg",
];

const layers = [
  {
    images: mriImages,
    layerId: "",
    options: {
      visible: true,
      opacity: 1,
      name: "MRI",
      viewport: {
        colormap: ""
      }
    },
    toolState: null // Tool state for each layer
  },
];

const leftMouseToolChain = [
  { name: "Pan", func: cornerstoneTools.PanTool, config: {} },
  { name: "Magnify", func: cornerstoneTools.MagnifyTool, config: {} },
  { name: "Angle", func: cornerstoneTools.AngleTool, config: {} },
  { name: "Wwwc", func: cornerstoneTools.WwwcTool, config: {} },
  { name: "Eraser", func: cornerstoneTools.EraserTool, config: {} }
];

function DICOMViewer(){
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const viewerRef = useRef(null);
  const leftMouseToolsRefs = useRef([]); // Ref for leftMouseTools per layer
  const switchImage = (newIndex) => {
    if (newIndex >= 0 && newIndex < mriImages.length) {
      setCurrentImageIndex(newIndex);
      updateTheImages(newIndex); // Update the displayed image
    }
  };
const loadImages = (index = 0) => {
    const promises = [];

    layers.forEach(function (layer) {
      if (layer.options.visible) {
        const loadPromise = cornerstone.loadAndCacheImage(layer.images[index]);
        promises.push(
          loadPromise.then((image) => {
            // Reset the viewport settings for each loaded image
            const viewport = cornerstone.getDefaultViewport(viewerRef.current, image);
            cornerstone.setViewport(viewerRef.current, viewport, layer.layerId);
            return image;
          })
        );
      }
    });

    return Promise.all(promises);
  };

   const updateTheImages = useCallback(
    async (index) => {
      const images = await loadImages(index);
      images.forEach((image, layerIndex) => {
        const layer = layers[layerIndex];
        cornerstone.setLayerImage(viewerRef.current, image, layer.layerId);

        // Set the active tool for the updated image
        if (layerIndex === index) {
          cornerstoneTools.setToolActive(leftMouseToolChain[0].name, {
            mouseButtonMask: 1,
          });
        } else {
          cornerstoneTools.setToolPassive(leftMouseToolChain[0].name, {
            mouseButtonMask: 1,
          });
        }

        cornerstone.updateImage(viewerRef.current, layerIndex);
      });
    },
    [viewerRef, leftMouseToolChain]
  );

  const [wheelY, setWheelY] = useState(0);
  const [opacity, setOpacity] = useState(0);
  const [layerIndex, setLayerIndex] = useState(1);
  const [isVisible, setIsVisible] = useState(true);
  const [colorMapList, setColorMapList] = useState(
    cornerstone.colors.getColormapsList()
  );
  const [color, setColor] = useState("");

  const leftMouseToolsRef = useRef(null);
  const [leftMouseTool, setLeftMouseTool] = useState(
    leftMouseToolChain[0].name
  );


  // Add a function to switch between images
  useEffect(() => {

    cornerstoneTools.external.cornerstone = cornerstone;
    cornerstoneTools.external.cornerstoneMath = cornerstoneMath;
    cornerstoneWebImageLoader.external.cornerstone = cornerstone;
    cornerstoneWADOImageLoader.external.cornerstone = cornerstone;
    cornerstoneWADOImageLoader.external.dicomParser = dicomParser;
    cornerstoneTools.external.Hammer = Hammer;
    cornerstoneTools.init();
    cornerstone.enable(viewerRef.current);
    init();
    setTools();
    setEventListeners();
    return () => {
      removeEventListeners();
    };

    function removeEventListeners() {}

    function setEventListeners() {
      viewerRef.current.addEventListener(
        "cornerstonetoolsmousedrag",
        (event) => {
          // console.log(event.detail)
        }
      );
      viewerRef.current.addEventListener(
        "cornerstonetoolsmousewheel",
        (event) => {
          // scroll forward
          if (event.detail.detail.deltaY < 0) {
            setWheelY((position) => {
              if (position >= 1) {
                position = 1;
              } else {
                position += 1;
              }
              updateTheImages(position);
              return position;
            });
          } else {
            // scroll back
            setWheelY((position) => {
              if (position <= 0) {
                position = 0;
              } else {
                position -= 1;
              }
              updateTheImages(position);
              return position;
            });
          }
        }
      );

      viewerRef.current.addEventListener(
        "cornerstoneactivelayerchanged",
        function (e) {
          const layer = cornerstone.getActiveLayer(viewerRef.current);
          const colormap = layer.viewport.colormap;
          const opacity = layer.options.opacity;
          const isVisible = layer.options.visible;

          unstable_batchedUpdates(() => {
            setOpacity(opacity);
            setIsVisible(isVisible);
            setColor(colormap);
          });
        }
      );
    }
    function setTools() {
      // zoom
      const zoomTool = cornerstoneTools.ZoomTool;
      cornerstoneTools.addTool(zoomTool, {
        configuration: {
          invert: false,
          preventZoomOutsideImage: false,
          minScale: 0.1,
          maxScale: 20.0
        }
      });
      cornerstoneTools.setToolActive("Zoom", { mouseButtonMask: 2 });

      for (let i = 0; i < leftMouseToolChain.length; i++) {
        if (i === 0) {
          // panning
          cornerstoneTools.addTool(leftMouseToolChain[i].func);
          cornerstoneTools.setToolActive(leftMouseToolChain[i].name, {
            mouseButtonMask: 1
          });
        } else {
          cornerstoneTools.addTool(leftMouseToolChain[i].func);
          cornerstoneTools.setToolPassive(leftMouseToolChain[i].name, {
            mouseButtonMask: 1
          });
        }
      }
    }

    async function init() {
      const images = await loadImages();
      images.forEach((image, index) => {
        const layer = layers[index];
        const layerId = cornerstone.addLayer(
          viewerRef.current,
          image,
          layer.options
        );
        layers[index].layerId = layerId;

        // segmantaion
        if (index === 1) {
          cornerstone.setActiveLayer(viewerRef.current, layerId);
        }

        // Display the first image
        cornerstone.updateImage(viewerRef.current);
      });
    }
  }, [updateTheImages]);

  const onClickToggleInterpolation = () => {
    const viewport = cornerstone.getViewport(viewerRef.current);
    viewport.pixelReplication = !viewport.pixelReplication;
    cornerstone.setViewport(viewerRef.current, viewport);
  };

  const onClickRotation = () => {
    const viewport = cornerstone.getViewport(viewerRef.current);
    viewport.rotation += 90;
    cornerstone.setViewport(viewerRef.current, viewport);
  };

  const onChangeVisibility = (event) => {
    setIsVisible((isVisible = true) => {
      isVisible = !isVisible;
      const layer = cornerstone.getActiveLayer(viewerRef.current);
      layer.options.visible = isVisible;
      if (isVisible) {
        updateTheImages(wheelY).then(() => {
          return isVisible;
        });
      } else {
        return isVisible;
      }
    });

    cornerstone.updateImage(viewerRef.current);
  };

  const onChangeOpacity = (event) => {
    const opacity = event.target.value;
    const layer = cornerstone.getActiveLayer(viewerRef.current);
    layer.options.opacity = opacity;
    cornerstone.updateImage(viewerRef.current);
    setOpacity(opacity);
  };

  const onChangeLayer = (event) => {
    const index = event.target.value;
    setLayerIndex(index);
    cornerstone.setActiveLayer(viewerRef.current, layers[index].layerId);
  };

  const onChangeColorMapList = (event) => {

    const color = event.target.value;
    const layer = cornerstone.getActiveLayer(viewerRef.current);
    layer.viewport.colormap = color;
    cornerstone.updateImage(viewerRef.current);
    setColor(color);
  };

  const onClickToggleInvert = (event) => {
    const viewport = cornerstone.getViewport(viewerRef.current);
    viewport.invert = !viewport.invert;
    cornerstone.setViewport(viewerRef.current, viewport);
  };

  const onChangeLTools = (event) => {
    const toolName = event.target.value;

    for (let i = 0; i < leftMouseToolChain.length; i++) {
      if (leftMouseToolChain[i].name === toolName) {
        // panning
        cornerstoneTools.addTool(leftMouseToolChain[i].func);
        cornerstoneTools.setToolActive(leftMouseToolChain[i].name, {
          mouseButtonMask: 1
        });
      } else {
        cornerstoneTools.addTool(leftMouseToolChain[i].func);
        cornerstoneTools.setToolPassive(leftMouseToolChain[i].name, {
          mouseButtonMask: 1
        });

        // You can make tool disabled
        // cornerstoneTools.setToolDisabled(leftMouseToolChain[i].name, {
        //   mouseButtonMask: 1
        // });
        cornerstone.updateImage(viewerRef.current);
      }
    }

    setLeftMouseTool(toolName);
  };

  return (
  <section className="container mt-4">
  <h3 className="text-center" style={{color: 'white', backgroundColor: '#0dcaf0'}}><i className="fa-solid fa-comment"
        aria-hidden="true" style={{color: 'red'}}></i> Dicom Viewer</h3>
    <div className="row mt-5">
      <div className="controls">
        <label htmlFor="layer">Active layer: </label>
        <select id="layer" onChange={onChangeLayer} value={layerIndex}>
          <option value={0}>MRI</option>
        </select>
        <label htmlFor="opacity" className="ms-3">Opacity:</label>
        <span id="opacity">{opacity}</span>
        <input
          type="range"
          onChange={onChangeOpacity}
          name="opacity"
          min="0"
          max="1"
          step="0.01"
          value={opacity}
          className="ms-3"
        />
        <label htmlFor="colorMap" className="ms-3">Color map:</label>
        <select id="colorMap" value={color} onChange={onChangeColorMapList} className="ms-3">
          <option value="">None</option>
          {colorMapList.map((colorMapItem, index) => (
            <option key={colorMapItem.name + index} value={colorMapItem.id}>
              {colorMapItem.name}
            </option>
          ))}
        </select>
        <label htmlFor="visible" className="ms-3">Visibility:</label>
        <input
          id="visible"
          type="checkbox"
          checked={isVisible}
          onChange={onChangeVisibility}
          className="ms-3"
        />
        <button id="rotate" onClick={onClickRotation} className="btn btn-info ms-3" >
          Rotate 90°
        </button>
        <button id="interpolation" onClick={onClickToggleInterpolation} className="btn btn-info ms-3" >
          Toggle Interpolation
        </button>
        <button id="invert" onClick={onClickToggleInvert} className="btn btn-info ms-3" >
          Toggle Invert
        </button>

        <form id="l-mouse-tools" ref={leftMouseToolsRefs} onChange={onChangeLTools}>
          <label>Select tools:</label>
          {leftMouseToolChain.map((tool) => (
            <Fragment key={tool.name}>
              <label htmlFor={tool.name}>{tool.name}</label>
              <input
                type="radio"
                name="l-mouse-tool"
                id={tool.name}
                value={tool.name}
                checked={tool.name === leftMouseTool}
              />
            </Fragment>
          ))}
        </form>
         <button onClick={() => switchImage(currentImageIndex - 1)}>
            Previous Image
          </button>
          <button onClick={() => switchImage(currentImageIndex + 1)}>
            Next Image
          </button>
      </div>
      <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
      }}
    >
      <div
        ref={viewerRef}
        id="viewer"
        style={{
          width: "600px",
          height: "600px"
        }}
        className="mt-3"
      />
    </div>
    </div>
    </section>
  );
}
export default DICOMViewer;